import type { MerchandiseQuote, StorefrontProduct } from '../api/client';
import type { PendingLine } from './pendingLine';

// What an order held, kept from the cart the moment it is placed, so the thank-you page can list it: the
// order the server returns carries no lines, and the cart is emptied as the order is placed. Prices are
// the server's own quote for those lines, never the page's sums.

/** One line of a placed order, as its confirmation lists it. */
export type OrderedLine = {
  title: string; option: string; quantity: number; total: number; image?: string;
  /** What a quantity offer took off the line, and the offer step's name, as the checkout listed them. */
  discountAmount?: number; offerLabel?: string;
};

/** What the order held when it was placed: the cart's lines, or Buy it now's one. */
export type PlacedCart = { lines: readonly PendingLine[]; products: readonly StorefrontProduct[]; quote: MerchandiseQuote | null };

// Reads the name of the option a line holds, or nothing for a product that has only its default one.
const optionName = (title: string | undefined): string => (title && !/^default/i.test(title) ? title : '');

// Lists the cart's lines as the order placed them, each priced by the server's quote.
export function orderedLines(cart: PlacedCart): OrderedLine[] {
  return cart.lines.flatMap((line) => {
    const product = cart.products.find((row) => row.slug === line.slug);
    const priced = cart.quote?.lines.find((row) => row.variantId === line.variantId);
    if (!product || !priced) return [];
    const variant = product.variants.find((row) => row.id === line.variantId);
    return [{
      title: product.title, option: optionName(variant?.title), quantity: priced.quantity, total: Number(priced.totalPrice), image: product.images[0]?.src,
      discountAmount: Number(priced.discountAmount ?? 0), offerLabel: priced.offerLabel
    }];
  });
}
